/**
 * @fileoverview Os campos do cadastro financeiro (03/11/2026): em Faturamento e
 * na contratação, cada uma com os estilos dela (`classes`) e o prefixo dos ids
 * (`idPrefix`: as duas ficam na tela ao mesmo tempo, o modal sobre a página).
 * Rótulo em cima, erro embaixo; o select na mesma altura dos campos.
 */

import React, { useEffect, useRef } from "react";
import { CircularProgress, MenuItem } from "@material-ui/core";

import ModernSelect from "../ModernSelect";
import { i18n } from "../../translate/i18n";
import { onlyDigits } from "./useBillingProfile";

const t = (key) => i18n.t(`billingProfile.${key}`);
const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

// Guarda só os dígitos e formata na tela (como o formulário de dados da empresa).
const applyMask = (digits, mask) => {
  let out = "";
  let index = 0;
  for (const ch of mask) {
    if (index >= digits.length) break;
    if (ch === "#") out += digits[index++];
    else out += ch;
  }
  return out;
};
export const fmtDocument = (value, personType) =>
  personType === "company" ? applyMask(onlyDigits(value).slice(0, 14), "##.###.###/####-##") : applyMask(onlyDigits(value).slice(0, 11), "###.###.###-##");
const fmtPhone = (value) => {
  const digits = onlyDigits(value).slice(0, 11);
  return applyMask(digits, digits.length <= 10 ? "(##) ####-####" : "(##) #####-####");
};
const fmtZip = (value) => applyMask(onlyDigits(value).slice(0, 8), "#####-###");

const BillingProfileFields = ({ classes, billing, idPrefix = "billing" }) => {
  const { form, errors, zipLoading, focusField, clearFocusField, setField, setZipCode } = billing;
  const root = useRef(null);
  const company = form.personType === "company";
  const id = (key) => `${idPrefix}-${key}`;

  useEffect(() => {
    if (!focusField || !root.current) return;
    // A UF é um select: o foco vai no botão dele (o id, ali, é o do rótulo).
    const node = root.current.querySelector(`[aria-labelledby~="${id(focusField)}"]`) || root.current.querySelector(`#${id(focusField)}`);
    if (node) node.focus();
    clearFocusField();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusField, clearFocusField]);

  const invalid = (key) => (errors[key] ? { "aria-invalid": true, "aria-describedby": id(`${key}-error`) } : {});
  const inputClass = (key) => `${classes.input} ${errors[key] ? classes.inputError : ""}`;
  const error = (key) =>
    errors[key] ? (
      <span className={classes.fieldError} id={id(`${key}-error`)} role="alert">
        {errors[key]}
      </span>
    ) : null;
  const text = (key, extra = {}) => (
    <>
      <input id={id(key)} className={inputClass(key)} value={form[key]} onChange={(event) => setField(key, event.target.value)} autoComplete="off" {...invalid(key)} {...extra} />
      {error(key)}
    </>
  );

  return (
    <div className={classes.grid} ref={root}>
      <div className={`${classes.field} ${classes.span2}`}>
        <span className={classes.label} id={id("person-type")}>{t("personType")}</span>
        <ModernSelect
          size="small"
          value={form.personType}
          onChange={(event) => setField("personType", event.target.value)}
          labelId={id("person-type")}
          MenuProps={{ disableScrollLock: true }}
          fullWidth
          className={classes.select}
        >
          <MenuItem value="company">{t("company")}</MenuItem>
          <MenuItem value="individual">{t("individual")}</MenuItem>
        </ModernSelect>
      </div>
      <div className={`${classes.field} ${classes.span4}`}>
        <label className={classes.label} htmlFor={id("name")}>{company ? t("nameCompany") : t("nameIndividual")}</label>
        {text("name", { maxLength: 160 })}
      </div>
      <div className={`${classes.field} ${classes.span2}`}>
        <label className={classes.label} htmlFor={id("document")}>{company ? "CNPJ" : "CPF"}</label>
        <input
          id={id("document")}
          className={inputClass("document")}
          inputMode="numeric"
          value={fmtDocument(form.document, form.personType)}
          onChange={(event) => setField("document", onlyDigits(event.target.value))}
          placeholder={company ? "00.000.000/0000-00" : "000.000.000-00"}
          autoComplete="off"
          {...invalid("document")}
        />
        {error("document")}
      </div>
      <div className={`${classes.field} ${classes.span2}`}>
        <label className={classes.label} htmlFor={id("email")}>{t("email")}</label>
        {text("email", { type: "email", inputMode: "email" })}
      </div>
      <div className={`${classes.field} ${classes.span2}`}>
        <label className={classes.label} htmlFor={id("phone")}>{t("phone")}</label>
        <input
          id={id("phone")}
          className={inputClass("phone")}
          inputMode="numeric"
          value={fmtPhone(form.phone)}
          onChange={(event) => setField("phone", onlyDigits(event.target.value))}
          placeholder="(11) 99999-9999"
          autoComplete="off"
          {...invalid("phone")}
        />
        {error("phone")}
      </div>
      <div className={`${classes.field} ${classes.span2}`}>
        <label className={classes.label} htmlFor={id("zipCode")}>
          {t("zipCode")}
          {zipLoading && <CircularProgress size={11} color="inherit" className={classes.zipBusy} aria-label={t("zipLoading")} />}
        </label>
        <input
          id={id("zipCode")}
          className={inputClass("zipCode")}
          inputMode="numeric"
          value={fmtZip(form.zipCode)}
          onChange={(event) => setZipCode(event.target.value)}
          placeholder="00000-000"
          autoComplete="off"
          {...invalid("zipCode")}
        />
        {error("zipCode")}
      </div>
      <div className={`${classes.field} ${classes.span4}`}>
        <label className={classes.label} htmlFor={id("street")}>{t("street")}</label>
        {text("street", { maxLength: 160 })}
      </div>
      <div className={`${classes.field} ${classes.span2}`}>
        <label className={classes.label} htmlFor={id("number")}>{t("number")}</label>
        {text("number", { maxLength: 20 })}
      </div>
      <div className={`${classes.field} ${classes.span4}`}>
        <label className={classes.label} htmlFor={id("complement")}>
          {t("complement")} <span className={classes.optional}>{t("optional")}</span>
        </label>
        {text("complement", { maxLength: 80 })}
      </div>
      <div className={`${classes.field} ${classes.span3}`}>
        <label className={classes.label} htmlFor={id("neighborhood")}>{t("neighborhood")}</label>
        {text("neighborhood", { maxLength: 80 })}
      </div>
      <div className={`${classes.field} ${classes.span2}`}>
        <label className={classes.label} htmlFor={id("city")}>{t("city")}</label>
        {text("city", { maxLength: 80 })}
      </div>
      <div className={classes.field}>
        <span className={classes.label} id={id("state")}>{t("state")}</span>
        <ModernSelect
          size="small"
          value={form.state}
          onChange={(event) => setField("state", event.target.value)}
          labelId={id("state")}
          MenuProps={{ disableScrollLock: true }}
          fullWidth
          className={`${classes.select} ${errors.state ? classes.selectError : ""}`}
        >
          {UFS.map((uf) => (
            <MenuItem key={uf} value={uf}>
              {uf}
            </MenuItem>
          ))}
        </ModernSelect>
        {error("state")}
      </div>
    </div>
  );
};

export default BillingProfileFields;
